import { FC, useContext, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { isTravelTimeSearchSelector } from '@zoopla/redux-reducer-filters';
import { SearchContext } from '@zoopla/listing-search-context';
import triggerTracking from '@blueprint/tracking';
import { Icon } from '@blueprint/ui';
import {
    AlertPanelWrapper,
    AlertPanelButtonLink,
    LeftAlignButtonWithIcon,
    PaddedDivider
} from './AlertPanel.styles';
import { AlertPanelProps } from './AlertPanel.types';
import { useGetMapViewUrl } from '@zoopla/listing-search/use-map-view-url';
import {
    Taxonomy,
    useTrackSegmentEvent,
    ANALYTICS_BASE_TAXONOMY,
    UserTaxonomy,
    updateUserTaxonomyKeys
} from '@zoopla/tracking';
import { getUserSelector } from '@zoopla/redux-reducer-user';

const createAlertBtnText = 'Create email alert';
const saveSearchBtnText = 'Save this search';
const travelTimeSearchBtnText = 'Travel time search';
const drawYourSearchBtnText = 'Draw your search';

const fireTrackingEvent = (label: string) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'search results',
        eventAction: 'clickthrough',
        eventLabel: label
    });
};

const AlertPanelAjax: FC<React.PropsWithChildren<AlertPanelProps>> = ({
    links,
    onAjaxLinkClick,
    onSimpleLinkClick,
    isLoggedIn,
    taxonomy
}) => {
    const {
        state: { userAlertId }
    } = useContext(SearchContext);

    const isSavedSearch = userAlertId !== null;
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);
    const userTaxonomy = useSelector(getUserSelector);

    const simpleLinkTravelTime = (evt: MouseEvent<HTMLElement>) => {
        onSimpleLinkClick &&
            onSimpleLinkClick(evt, 'search results', 'travel_time_search');
    };

    const ajaxLinkCreateAlert = async (evt: MouseEvent<HTMLElement>) => {
        onAjaxLinkClick &&
            (await onAjaxLinkClick(evt, 'search results', 'create_alert'));

        trackSegmentEvent(
            'Click Email Alert',
            {
                ...ANALYTICS_BASE_TAXONOMY,
                ...(taxonomy as unknown as Taxonomy),
                ...(updateUserTaxonomyKeys(
                    userTaxonomy
                ) as unknown as UserTaxonomy),
                signedInStatus: isLoggedIn ? 'signed in' : 'signed out',
                location: taxonomy?.areaName
            },
            undefined
        );
    };

    const ajaxLinkSaveSearch = async (evt: MouseEvent<HTMLElement>) => {
        onAjaxLinkClick &&
            (await onAjaxLinkClick(evt, 'search results', 'save_search'));

        trackSegmentEvent(
            'Click Save Search',
            {
                ...ANALYTICS_BASE_TAXONOMY,
                ...(taxonomy as unknown as Taxonomy),
                ...(updateUserTaxonomyKeys(
                    userTaxonomy
                ) as unknown as UserTaxonomy),
                signedInStatus: isLoggedIn ? 'signed in' : 'signed out',
                location: taxonomy?.areaName
            },
            undefined
        );
    };

    const mapViewUrl = useGetMapViewUrl(true);

    const trackSegmentEvent = useTrackSegmentEvent();

    return (
        <AlertPanelWrapper>
            {!isSavedSearch ? (
                <>
                    <AlertPanelButtonLink
                        variant="secondary"
                        data-testid="create-email-alert-button"
                        data-url={links?.createAlert || ''}
                        isFullWidth
                        onClick={ajaxLinkCreateAlert}
                    >
                        <Icon iconId="email_outline" />
                        {createAlertBtnText}
                    </AlertPanelButtonLink>
                    <AlertPanelButtonLink
                        variant="secondary"
                        data-testid="save-search-button"
                        data-url={links?.saveSearch || ''}
                        isFullWidth
                        onClick={ajaxLinkSaveSearch}
                    >
                        <Icon iconId="save_outline" />
                        {saveSearchBtnText}
                    </AlertPanelButtonLink>
                </>
            ) : null}
            <PaddedDivider variant="horizontal" />
            {!isTravelTimeSearch ? (
                <LeftAlignButtonWithIcon
                    variant="tertiary"
                    data-testid="travel-time-search-button-link"
                    data-url="/travel-time/"
                    isFullWidth
                    onClick={simpleLinkTravelTime}
                >
                    <Icon iconId="history" />
                    {travelTimeSearchBtnText}
                </LeftAlignButtonWithIcon>
            ) : null}
            <LeftAlignButtonWithIcon
                variant="tertiary"
                data-testid="draw-your-search-button-link"
                href={mapViewUrl}
                rel="nofollow"
                onClick={() => fireTrackingEvent('draw_search')}
                isFullWidth
            >
                <Icon iconId="draw" />
                {drawYourSearchBtnText}
            </LeftAlignButtonWithIcon>
        </AlertPanelWrapper>
    );
};

export default AlertPanelAjax;
